import {
  size,
  colors,
  mediaQueries,
  typography,
  Row as RawRow,
  Container as RawContainer,
} from '@everlywell/leaves';
import GatsbyLink from 'components/Link';
import styled from 'styled-components';

export const SectionWrapper = styled.div`
  display: block;
  width: 100%;
  overflow-x: hidden;

  ${mediaQueries.forDesktopUp} {
    display: flex;
  }
`;

export const Headline = styled.h1`
  ${typography.h1Text};
`;

export const Description = styled.p`
  ${typography.bodyText};
  margin-top: ${size.md}px;
  margin-bottom: ${size.xl3}px;
`;

export const Container = styled(RawContainer)`
  position: relative;
  overflow: visible;
  padding-bottom: 950px;

  @media screen and (max-width: 768px) {
    padding-bottom: 0; /* Let content flow naturally on mobile */
    min-height: auto;
  }
  
  ${mediaQueries.forTabletHorizontalUp} {
    padding-bottom: 950px;
  }
  
  ${mediaQueries.forDesktopUp} {
    padding-bottom: 950px;
  }
`;

export const Row = styled(RawRow)`
  margin: 0;
`;

export const IFrame = styled.iframe`
  position: absolute;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  max-height: calc(100% - 258px);

  ${mediaQueries.forTabletHorizontalUp} {
    margin: 0 24px;
  }

  ${mediaQueries.forDesktopUp} {
    margin: 0 48px;
  }
`;

export const HubSpotFormContainer = styled.div`
  position: relative;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 ${size.sm}px;
  box-sizing: border-box;

  @media screen and (max-width: 768px) {
    position: relative;
    padding: 0 ${size.sm}px;
    width: 100%;
    margin-bottom: ${size.xl1}px;
  }
  
  ${mediaQueries.forTabletHorizontalUp} {
    position: absolute;
    left: 0;
    right: 0;
    height: 100%;
    max-height: calc(100% - 258px);
    padding: 0 ${size.lg}px;
    margin: 0 24px;
    width: calc(100% - 48px);
  }

  ${mediaQueries.forDesktopUp} {
    padding: 0 48px;
    margin: 0 48px;
    width: calc(100% - 96px);
  }

  /* HubSpot form styling */
  .hs-form {
    width: 100%;
    max-width: 100%;
    font-family: inherit;
    box-sizing: border-box;
    
    @media screen and (max-width: 768px) {
      padding: 0;
    }
  }

  .hs-form fieldset {
    max-width: 100% !important;
    border: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    box-sizing: border-box !important;
  }

  .hs-form fieldset.form-columns-1,
  .hs-form fieldset.form-columns-2,
  .hs-form fieldset.form-columns-3 {
    margin: 0 !important;
  }

  /* Form field groups */
  .hs-form-field {
    margin-bottom: ${size.lg}px !important;
    
    @media screen and (max-width: 768px) {
      margin-bottom: ${size.md}px !important;
    }
  }

  /* Labels */
  .hs-form label {
    ${typography.bodyText}
    color: ${colors.gray5};
    display: block !important;
    margin-bottom: ${size.xs1}px !important;
    font-weight: 500;
    word-wrap: break-word;
  }

  .hs-form label span {
    color: ${colors.gray5} !important;
  }

  /* Required field indicator */
  .hs-form .hs-form-required {
    color: ${colors.red3} !important;
    margin-left: 4px;
  }

  /* Input fields, selects, and textareas */
  .hs-form input[type="text"],
  .hs-form input[type="email"],
  .hs-form input[type="tel"],
  .hs-form input[type="number"],
  .hs-form input[type="date"],
  .hs-form select,
  .hs-form textarea {
    width: 100% !important;
    max-width: 100% !important;
    padding: ${size.sm}px ${size.md}px !important;
    border: 1px solid ${colors.gray2} !important;
    border-radius: 4px !important;
    font-size: 16px !important;
    line-height: 24px !important;
    color: ${colors.gray5} !important;
    background-color: ${colors.white} !important;
    font-family: inherit !important;
    transition: all 0.2s ease !important;
    box-sizing: border-box !important;
    -webkit-appearance: none; /* Prevent iOS zoom on focus */
    
    @media screen and (max-width: 768px) {
      padding: ${size.sm}px ${size.sm}px !important;
      font-size: 16px !important; /* Prevents zoom on iOS */
    }
    
    &:hover {
      border-color: ${colors.teal3} !important;
    }
    
    &:focus {
      outline: none !important;
      border-color: ${colors.teal4} !important;
      box-shadow: 0 0 0 3px rgba(0, 115, 119, 0.1) !important;
    }

    &::placeholder {
      color: ${colors.gray3} !important;
    }
  }

  .hs-form select {
    appearance: none !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23007377' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e") !important;
    background-repeat: no-repeat !important;
    background-position: right ${size.sm}px center !important;
    background-size: 20px !important;
    padding-right: ${size.xl2}px !important;
  }

  .hs-form textarea {
    min-height: 120px !important;
    resize: vertical !important;
  }

  /* Checkbox and Radio buttons */
  .hs-form input[type="checkbox"],
  .hs-form input[type="radio"] {
    margin-right: ${size.xs1}px !important;
    width: auto !important;
    position: relative !important;
    top: 2px !important;
  }

  .hs-form .inputs-list {
    padding: 0 !important;
    margin: 0 !important;
    list-style: none !important;
  }

  .hs-form .inputs-list label {
    display: flex !important;
    align-items: flex-start !important;
    margin-bottom: ${size.sm}px !important;
    font-weight: 400 !important;
  }

  .hs-form .hs-input[type="checkbox"],
  .hs-form .hs-input[type="radio"] {
    margin-top: 4px !important;
    margin-right: ${size.sm}px !important;
  }

  /* Error messages */
  .hs-form .hs-error-msgs {
    margin-top: ${size.xs1}px !important;
    margin-bottom: 0 !important;
    list-style: none !important;
    padding: 0 !important;
  }

  .hs-form .hs-error-msg {
    color: ${colors.red3} !important;
    font-size: 14px !important;
    line-height: 20px !important;
  }

  /* Submit button */
  .hs-form .hs-submit {
    margin-top: ${size.xl1}px !important;
    
    @media screen and (max-width: 768px) {
      margin-top: ${size.lg}px !important;
    }
  }

  .hs-form input[type="submit"],
  .hs-form .hs-button {
    ${typography.buttonText}
    background-color: ${colors.teal4} !important;
    color: ${colors.white} !important;
    border: 2px solid ${colors.teal4} !important;
    border-radius: 4px !important;
    padding: ${size.sm}px ${size.xl1}px !important;
    font-weight: 600 !important;
    cursor: pointer !important;
    transition: all 0.2s ease !important;
    display: inline-block !important;
    text-align: center !important;
    text-decoration: none !important;
    width: auto !important;
    min-width: 120px !important;
    
    @media screen and (max-width: 768px) {
      padding: ${size.sm}px ${size.lg}px !important;
      font-size: 16px !important;
      width: 100% !important;
      max-width: 100% !important;
    }
    
    &:hover {
      background-color: ${colors.teal5} !important;
      border-color: ${colors.teal5} !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
    
    &:active {
      transform: translateY(0);
    }
    
    &:focus {
      outline: none !important;
      box-shadow: 0 0 0 3px rgba(0, 115, 119, 0.2) !important;
    }
  }

  /* Legal consent text */
  .hs-form .legal-consent-container {
    margin-top: ${size.lg}px !important;
    
    @media screen and (max-width: 768px) {
      margin-top: ${size.md}px !important;
    }
    
    .hs-richtext {
      font-size: 14px !important;
      line-height: 20px !important;
      color: ${colors.gray4} !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      
      @media screen and (max-width: 768px) {
        font-size: 13px !important;
        line-height: 18px !important;
      }
      
      a {
        color: ${colors.teal4} !important;
        text-decoration: underline !important;
        word-break: break-word !important;
        
        &:hover {
          color: ${colors.teal5} !important;
        }
      }
    }
  }

  /* reCAPTCHA styling */
  .hs-form .hs-recaptcha {
    margin-top: ${size.lg}px !important;
    margin-bottom: ${size.lg}px !important;
    
    @media screen and (max-width: 768px) {
      margin-top: ${size.md}px !important;
      margin-bottom: ${size.md}px !important;
      transform: scale(0.9);
      transform-origin: left center;
      max-width: 100%;
    }
  }

  /* Thank you message */
  .submitted-message {
    ${typography.bodyText}
    color: ${colors.green4};
    background-color: ${colors.green1};
    padding: ${size.md}px;
    border-radius: 4px;
    margin-top: ${size.lg}px;
  }

  /* Form columns layout */
  .hs-form .form-columns-2 {
    display: flex !important;
    gap: ${size.lg}px !important;
    
    .hs-form-field {
      flex: 1 !important;
      min-width: 0 !important; /* Prevent flex items from overflowing */
    }
    
    @media screen and (max-width: 768px) {
      flex-direction: column !important;
      gap: 0 !important;
    }
  }

  .hs-form .form-columns-3 {
    display: flex !important;
    gap: ${size.lg}px !important;
    
    .hs-form-field {
      flex: 1 !important;
      min-width: 0 !important; /* Prevent flex items from overflowing */
    }
    
    @media screen and (max-width: 768px) {
      flex-direction: column !important;
      gap: 0 !important;
    }
  }

  /* Loading state */
  .hs-form.hs-form-submitting {
    opacity: 0.7;
    pointer-events: none;
  }
`;

export const LeftPanel = styled.div`
  width: 100%;
  min-height: 1183px;
  padding-top: ${size.xl1}px;
  overflow-y: auto;
  overflow-x: hidden;
  
  @media screen and (max-width: 768px) {
    min-height: auto;
    padding-top: ${size.lg}px;
  }

  ${mediaQueries.forTabletHorizontalUp} {
    padding-top: ${size.xl5}px;
  }

  ${mediaQueries.forDesktopUp} {
    width: 58.34%;
  }
`;

export const RightPanel = styled.div`
  width: 100%;
  background: #f9f6f2;
  padding: ${size.xl3}px 42px ${size.xl3}px ${size.lg}px;
  box-sizing: border-box;

  ${mediaQueries.forTabletHorizontalUp} {
    padding: ${size.xl1}px ${size.xl4}px;
  }

  ${mediaQueries.forDesktopUp} {
    width: 41.66%;
    padding: 167px 0 0 111px;
  }
`;

export const SubTitle = styled.h2`
  ${typography.overlineText}
  color: ${colors.teal4};
  font-weight: ${typography.weight.bold};
`;

export const SubHeadline = styled.span`
  ${typography.bodyText}
  margin-top: ${size.sm}px;
  display: block;
  line-height: 29px;

  ${mediaQueries.forTabletVerticalDown} {
    font-size: ${size.md}px;
    line-height: 26px;
  }
`;

export const Content = styled.p`
  ${typography.captionText}
`;

export const PatientWrapper = styled.div`
  margin-top: ${size.xl4}px;
`;

export const MediaWrapper = styled.div`
  margin-top: ${size.xl2}px;

  ${mediaQueries.forTabletHorizontalUp} {
    margin-top: ${size.xl4}px;
  }
`;

export const LinkList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const IconWrapper = styled.div`
  color: ${colors.teal4};
  margin-left: 8px;
  position: relative;
  top: 7px;

  svg {
    width: 7px;
    height: 12px;
  }
`;

export const Link = styled(GatsbyLink)`
  ${typography.buttonText}
  color: ${colors.teal4};
  text-decoration: underline;
  margin-top: ${size.md}px;
  display: flex;

  &:hover,
  &:focus {
    color: ${colors.green5};

    ${IconWrapper} {
      color: ${colors.green5};
    }
  }
`;
